import { FetchResult } from "@apollo/client";
import React, {
    DependencyList,
    ReactNode, useContext,
    useEffect,
} from "react";
import { GraphQLError } from "graphql";
import { LogErrorInput, LogLevel } from "../../__generated__/api";
import LogSaver, { createContextData, errorToInput } from "./LogSaver";
import { ARTIFICIAL_INTELLIGENCE_ID } from "../ticket-state/ticket-item";

export { createContextData } from "./LogSaver";

const LOG_LEVELS = {
    [LogLevel.Trace]: 0,
    [LogLevel.Debug]: 10,
    [LogLevel.Info]: 20,
    [LogLevel.Warning]: 30,
    [LogLevel.Error]: 40,
    [LogLevel.Fatal]: 50,
};
const LOG_LEVEL = (process.env.LOG_LEVEL ?? "DEBUG") as LogLevel;
const PRINT_LEVEL = LOG_LEVELS[LOG_LEVEL];

function parseMessageParams(
    dataOrMessage: Record<string, unknown> | string,
    message?: string,
) {
    let data: Record<string, unknown> | undefined;
    let msg = message;
    if (typeof dataOrMessage === "object") {
        data = dataOrMessage;
    } else if (typeof dataOrMessage === "string") {
        msg = dataOrMessage;
    }
    return {
        message: msg as string,
        data,
    };
}

function parseParams(
    errorOrData: Error | Record<string, unknown>,
    dataOrMessage: Record<string, unknown> | string,
    message?: string,
) {
    const error = errorOrData instanceof Error ? errorOrData : undefined;
    let data: Record<string, unknown> | undefined;
    if (!(errorOrData instanceof Error)) {
        data = errorOrData;
    } else if (typeof dataOrMessage !== "string") {
        data = dataOrMessage;
    }
    let msg = message;
    if (!msg && typeof dataOrMessage === "string") {
        msg = dataOrMessage;
    }
    return {
        message: msg as string,
        data: {
            error,
            ...data,
        },
    };
}

function createError(data: Record<string, unknown>): LogErrorInput | undefined {
    const error = data.err || data.error;
    return error instanceof Error ? errorToInput(error) : undefined;
}

type PromiseType<T = unknown> = null | undefined | Promise<T> | (() => Promise<T>) | (() => T) | (() => void);
export declare type InferType<T> = T extends Promise<infer A> ? A : T extends (() => Promise<infer A>) ? A : T;

export declare type Fields<T> = {
    [P in keyof T]: InferType<T[P]>;
};

const METRIC_TYPES = {
    PAGE_VIEW: "page-view",
    ARTIFICIAL_INTELLIGENCE: "artificial-intelligence",
    ARTICLES: "articles",
    AGENT: "agent",
    TICKETS: "tickets",
    UPDATES: "updates",
};
export class LoggingHandler {
    public static readonly instance = new LoggingHandler(new LogSaver(), { component: "Default" });

    public static readonly Metrics = METRIC_TYPES;

    // eslint-disable-next-line no-useless-constructor
    constructor(private saver: LogSaver, private data: Record<string, unknown>) {
        // declare private
    }

    child(data: Record<string, unknown>, component: string): LoggingHandler {
        return new LoggingHandler(this.saver, {
            ...this.data,
            ...data,
            component,
        });
    }

    public async wrap<T>(handler: () => T | Promise<T>): Promise<T | undefined> {
        try {
            return await handler();
        } catch (err) {
            this.error({ err }, "Unhandled error");
            return undefined;
        }
    }

    public async time<T>(data: Record<string, string>, message: string, handler: () => T | Promise<T>): Promise<T | undefined> {
        const start = performance.now();
        try {
            return await handler();
        } catch (err) {
            this.error({ err }, "Unhandled error");
            return undefined;
        } finally {
            const duration = performance.now() - start;
            this.info({ ...data, duration }, message);
        }
    }

    public async all<T extends PromiseType[]>(...handlers: T): Promise<Fields<T>> {
        const all = await Promise.all(handlers.map(handler => {
            if (handler == null) {
                return handler;
            }
            return this.wrap(typeof handler === "function" ? handler : () => handler);
        }));
        return all as Fields<T>;
    }

    public async withRetries<T>(handler: () => Promise<FetchResult<T>>): Promise<T | undefined | null> {
        let lastErrors: readonly Error[] = [];
        for (let attempt = 0; attempt < 4; attempt += 1) {
            // eslint-disable-next-line no-await-in-loop
            const { data, errors } = await handler();
            if (!errors) {
                return data;
            }
            lastErrors = errors.map(error => new GraphQLError(error.message, error));
        }
        this.error({ errors: lastErrors }, "Failed to execute query");
        return null;
    }

    public async nextTick<T>(handler: () => (Promise<T> | T)): Promise<T> {
        return new Promise<T>(resolve => {
            setTimeout(async () => {
                try {
                    resolve(await handler());
                } catch (err) {
                    this.error({ err }, "Unhandled error");
                }
            }, 1);
        });
    }

    private addMessage(
        level: LogLevel,
        rawData: Record<string, unknown> | undefined,
        message: string,
        logger: ((...data: unknown[]) => void),
    ) {
        const data = rawData ?? {};
        const timestamp = new Date();
        this.saver.addLog({
            level,
            message,
            timestamp,
            component: data.component as string ?? "Default",
            data: {
                ...data,
                ...createContextData(),
            },
            error: createError(data),
        });
        if (LOG_LEVELS[level] >= PRINT_LEVEL) {
            logger(timestamp.toLocaleString(), level, message, data && Object.keys(data).isNotEmpty ? data : "");
        }
    }

    public metric(path: string[]): void {
        this.addMetric(path);
    }

    // eslint-disable-next-line no-dupe-class-members
    wrapMetric<TResult = void, TArgs extends unknown[] = []>(
        path: string[],
        handler?: (...args: TArgs) => TResult,
    ): ((...args: TArgs) => TResult) {
        return (...args: TArgs) => {
            this.addMetric(path);
            return handler?.(...args) as TResult;
        };
    }

    // eslint-disable-next-line no-dupe-class-members
    wrapMetricWithSnapshot<TResult = void, TArgs extends unknown[] = []>(
        path: string[],
        handler?: (...args: TArgs) => TResult,
    ): ((...args: TArgs) => TResult) {
        return (...args: TArgs) => {
            this.addMetric(path);
            this.snapshot();
            return handler?.(...args) as TResult;
        };
    }

    private addMetric(path: string[]) {
        const timestamp = new Date();
        // eslint-disable-next-line no-console
        console.debug(timestamp.toLocaleString(), "Metric", path);
        this.saver.addMetric({
            path,
            timestamp,
        });
    }

    public useMetric(path: string[], dependency: DependencyList = []) {
        this.useMetricWithCondition(path, () => true, dependency);
    }

    public useMetricWithCondition(path: string[], condition: () => boolean, dependency: DependencyList = []) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (condition()) {
                this.addMetric(path);
            }
        }, dependency);
    }

    public snapshot() {
        this.saver.snapshot();
    }

    trace(message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    trace(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    trace(dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const params = parseMessageParams(dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Trace, params.data, params.message, console.trace);
    }

    debug(message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    debug(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    debug(dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const params = parseMessageParams(dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Debug, params.data, params.message, console.debug);
    }

    info(message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    info(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    info(dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const params = parseMessageParams(dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Info, params.data, params.message, console.info);
    }

    warn(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    warn(error: Error, data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    warn(errorOrData: Error | Record<string, unknown>, dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const output = parseParams(errorOrData, dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Warning, output.data, output.message, console.warn);
    }

    error(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    error(error: Error, data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    error(errorOrData: Error | Record<string, unknown>, dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const output = parseParams(errorOrData, dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Error, output.data, output.message, console.error);
    }

    fatal(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    fatal(error: Error, data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    fatal(errorOrData: Error | Record<string, unknown>, dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const output = parseParams(errorOrData, dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Fatal, output.data, output.message, console.error);
    }
}

LoggingHandler.instance.info({
    environment: {
        version: window.FOQAL_VERSION_NUMBER,
        hostname: window.location.host,
        path: window.location.pathname,
    },
}, "Starting...");

export const LoggingContext = React.createContext<LoggingHandler>(LoggingHandler.instance);

export function LoggingProvider({ children }: {children: ReactNode}): (ReactNode | null) {
    return (
        <LoggingContext.Provider value={LoggingHandler.instance}>
            {children}
        </LoggingContext.Provider>
    );
}

export default function useLogging(): LoggingHandler {
    return useContext(LoggingContext) ?? LoggingHandler.instance;
}
